<template>
  <RootPageLayout>
    <div
      class="mb-6 mt-4 flex flex-col items-center justify-center"
    >
      <p class="font-semibold">
        {{ merchantName }}
      </p>
    </div>
    <div class="space-y-6">
      <MoreMenuSection
        v-if="
          youlendOffer &&
          organizationConfig?.businessFundingEnabled
        "
      >
        <div class="pb-2">
          <IconAppAdvancePlusBig
            class="text-background-fg"
          />
          <p class="text-sm font-medium">
            You're prequalified for an advance of your
            payments.
          </p>
        </div>
        <MoreMenuSubsection>
          <MoreMenuItemAction
            inverted
            :to="{ name: 'settingsBusinessFunding' }"
          >
            <template #icon>
              <IconBusinessFunding
                class="size-8 animate-grow"
              />
            </template>
            <template #label>
              Grow with <b>Advance+</b> now
            </template>
          </MoreMenuItemAction>
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Web Portal
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemAction
            :icon="IconPortal"
            label="Web Portal"
            description="Send access link"
            @click="handleRequestPortalLink"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Features
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            :icon="IconEnableProducts"
            label="Products"
            description="Enable products"
            :model-value="isProductsEnabled"
            supervisor-required
            @update:model-value="handleToggleProducts"
          />
          <MoreMenuItemToggle
            :icon="IconEnableCashiers"
            label="Cashiers"
            description="Enable cashiers"
            :model-value="isCashiersEnabled"
            supervisor-required
            @update:model-value="handleToggleCashiers"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection v-if="isCashiersEnabled">
        <MoreMenuSectionTitle>
          Cashiers
        </MoreMenuSectionTitle>

        <MoreMenuSubsection>
          <MoreMenuItemInfo
            label="Current Cashier"
            :value="currentCashier"
          />
          <MoreMenuItemAction
            :icon="IconChangeCashier"
            label="Change cashier"
            description="Switch to a different cashier"
            @click="clearTokens"
          />
          <MoreMenuItemAction
            :icon="IconManageCashier"
            label="Manage cashiers"
            description="Create and edit cashiers"
            :to="{ name: 'settingsCashierIndex' }"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection v-if="isProductsEnabled">
        <MoreMenuSectionTitle>
          Products
        </MoreMenuSectionTitle>

        <MoreMenuSubsection>
          <MoreMenuItemToggle
            v-model="gridModeEnabled"
            :icon="IconGridMode"
            label="Grid"
            description="Enable products grid view"
          />
          <MoreMenuItemToggle
            v-model="sparseModeEnabled"
            :disabled="!gridModeEnabled"
            :icon="IconSparseMode"
            label="2 Column Mode"
            description="Grid mode displays bigger products and categories"
          />
          <MoreMenuItemAction
            :icon="IconManageProducts"
            label="Manage Products"
            description="Create and edit products"
            :to="{ name: 'settingsProductIndex' }"
          />

          <MoreMenuItemAction
            :icon="IconManageCategories"
            label="Manage Categories"
            description="Create and edit product categories"
            :to="{ name: 'settingsCategoryIndex' }"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle
          >Payments</MoreMenuSectionTitle
        >
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            v-if="isProductsEnabled"
            :model-value="tenant.isSplitPaymentsEnabled"
            :icon="IconSplit"
            label="Split Payments"
            description="Enable split payments"
            supervisor-required
            @update:model-value="
              (value) =>
                updateSetting(
                  'isSplitPaymentsEnabled',
                  value
                )
            "
          />
          <MoreMenuItemToggle
            v-if="gratuityIsAvailable"
            :model-value="isGratuityEnabled"
            :icon="IconGratuity"
            label="Gratuity"
            description="Enable gratuity"
            supervisor-required
            @update:model-value="onToggleEnableGratuity"
          />
          <MoreMenuItemToggle
            :model-value="isCashEnabled"
            :icon="IconCash"
            label="Cash"
            description="Enable cash payments"
            supervisor-required
            @update:model-value="onToggleEnableCash"
          />
          <MoreMenuItemAction
            v-if="isProductsEnabled"
            :icon="IconReceipt"
            label="Service Charge Settings"
            description="Manage service charge and defaults"
            :to="{ name: 'settingsServiceCharge' }"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Transactions
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemAction
            v-if="isUnlinkedRefundsEnabled"
            :icon="IconRefund"
            label="Unlinked Refund"
            description="Issue a manual refund"
            @click="handleOpenUnlinkedRefundDrawer"
          />

          <MoreMenuItemAction
            :icon="IconReceiptFilled"
            label="Raw Transactions"
            description="List of all transactions"
            :to="{ name: 'settingsTransactions' }"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>General</MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemAction
            :icon="IconChangePin"
            label="Change Supervisor Pin"
            description="Edit merchant supervisor Pin"
            @click="handleClickChangeSupervisorPin"
          />
          <MoreMenuItemAction
            :icon="IconManageCashier"
            label="Advanced Settings"
            description="Advanced configuration options"
            :to="{ name: 'settingsAdvancedOptions' }"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Reports
        </MoreMenuSectionTitle>

        <MoreMenuSubsection>
          <MoreMenuItemAction
            :icon="IconXReport"
            label="Sales Summary"
            :description="
              printingSupported
                ? `Print today's cash/card totals`
                : `View today's cash/card totals`
            "
            @click="handleEndOfDayReportClick"
          />
          <MoreMenuItemAction
            v-if="platform != 'paxEvo' && printingSupported"
            :icon="IconXReport"
            label="Card X Report"
            description="Print card mid-shift report"
            @click="handlePrintXReportClick"
          />
          <MoreMenuItemAction
            :icon="IconZReport"
            label="Card Z Report"
            description="Perform card end of day report"
            @click="handleZReportClick"
          />
          <MoreMenuItemAction
            :icon="IconZReport"
            label="Card Z Report History"
            description="View card end of day reports"
            :to="{ name: 'settingsZReportHistory' }"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Network
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemInfo
            label="Network Status"
            :value="
              isOnline ? 'connected' : 'not connected'
            "
          />
          <MoreMenuItemInfo
            label="Connection Type"
            :value="connectionType"
          />

          <MoreMenuItemAction
            :icon="IconWifi"
            label="WIFI Settings"
            description="Access your device's wifi settings"
            @click="INetworkSettings.openWifiSettings"
          />
          <MoreMenuItemAction
            :icon="IconCellular"
            label="Cellular Settings"
            description="Access your device's cellular settings"
            @click="INetworkSettings.openCellularSettings"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Device
        </MoreMenuSectionTitle>

        <MoreMenuSubsection>
          <MoreMenuItemInfo
            label="Merchant ID"
            :value="midToDisplay"
          />
          <MoreMenuItemInfo
            label="Serial Number"
            :value="serialNumber"
          />

          <MoreMenuItemInfo
            label="App Version"
            :value="appVersion"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection>
        <MoreMenuSubsection>
          <MoreMenuItemAction
            :icon="IconExitApp"
            label="Exit"
            description="Close the application"
            @click="IActions.exitApp"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>
    </div>
  </RootPageLayout>

  <ChangePinDrawer ref="changePinDrawerRef" />
  <EndOfDayReportDialog
    :is-open="endOfDayReportIsOpen"
    :report="endOfDayReport"
    @close="endOfDayReportIsOpen = false"
  />
  <PortalEmailListDrawer ref="portalEmailListDrawerRef" />
  <SettingsUnlinkedRefundDrawer
    ref="unlinkedRefundDrawerRef"
  />
  <PositiveZReportDrawer
    :is-open="zReportDrawerIsOpen"
    @close="zReportDrawerIsOpen = false"
  />
</template>

<script setup lang="ts">
import {
  IconGears as IconManageCashier,
  IconGears as IconManageProducts,
  IconGears as IconManageCategories,
  IconExitApp,
  IconGridMode,
  IconReceipt,
  IconRefund,
  IconReceiptFilled,
  IconChangePin,
  IconWifi,
  IconCellular,
  IconSparseMode,
  IconGratuity,
  IconXReport,
  IconZReport,
  IconCash,
  IconEnableProducts,
  IconEnableCashiers,
  IconSplit,
  IconPortal,
  IconChangeCashier,
  IconBusinessFunding,
  IconAppAdvancePlusBig,
} from "@/icons"

import { INetworkSettings } from "@/androidPlugins/INetworkSettings"
import { printPositiveXReport } from "@/services/reconciliation/printPositiveXReport"
import { IReports } from "@/androidPlugins/IReports"
import { partialUpdateSettings } from "@/services/settings/partialUpdateSettings"
import { useNetwork } from "@vueuse/core"
import { retreiveEndOfDayReport } from "@/services/reports/retreiveEndOfDayReport"
import { IActions } from "@/androidPlugins/IActions"
import type { TenantState } from "@/composables/useTenant"
import { useOrganizationConfig } from "@/services/organizations/retrieveOrganizationConfig"
import { logZReport } from "@/services/reconciliation/logZReport"
import { formatEvoPaymentsZReport } from "@/services/reconciliation/formatEvoPaymentsZReport"
import { useCashiers } from "@/services/cashiers/listCashiers"
import * as Sentry from "@sentry/vue"
import {
  formatEndOfDayReport,
  printEndOfDayReport,
} from "@/services/reconciliation/printEndOfDayReport"
import { IPrinter } from "@/androidPlugins/IPrinter"
import { ApiError } from "@/services/api"
import PositiveZReportDrawer from "@/components/PositiveZReportDrawer.vue"

const router = useRouter()
const route = useRoute()
const { isOnline, type: connectionType } = useNetwork()
const { clearTokens, isSupervisor, cashierId } = useAuth()
const {
  getById: getCashierById,
  isLoading: isCashiersLoading,
} = useCashiers()
const { gridModeEnabled } = useGridMode()
const { sparseModeEnabled } = useSparseMode()
const { reveal: revealSupervisorLoginDrawer } =
  useSupervisorLoginDrawer()
const {
  serialNumber,
  appVersion,
  merchantId,
  platform,
  printingSupported,
} = useDevice()
const {
  tenant,
  name: merchantName,
  isProductsEnabled,
  isCashiersEnabled,
  isGratuityEnabled,
  isCashEnabled,
  isDemo,
  setTenantState,
  isUnlinkedRefundsEnabled,
  youlendOffer,
  gratuityIsAvailable,
} = useTenant()
const { startLoading, stopLoading } =
  usePageBlockingLoading()

const { getOverrideMid } = useOverrideMid()
const midToDisplay = ref(merchantId.value)
onMounted(async () => {
  const overrideMid =
    import.meta.env.VITE_ENVIRONMENT === "production"
      ? null
      : await getOverrideMid()
  midToDisplay.value = overrideMid ?? merchantId.value
})

const { organizationConfig } = useOrganizationConfig()

const currentCashier = computed(() =>
  isCashiersLoading.value
    ? "-"
    : (getCashierById(cashierId).value?.username ??
      "Default")
)

const changePinDrawerRef = useTemplateRef(
  "changePinDrawerRef"
)
const handleClickChangeSupervisorPin = async () => {
  if (isDemo.value) {
    toast.error(
      "Changing supervisor PIN disabled for demo client"
    )
    return
  }

  if (!isSupervisor.value) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()
    if (isCanceled) return
  }

  await changePinDrawerRef.value?.reveal()
}

const portalEmailListDrawerRef = useTemplateRef(
  "portalEmailListDrawerRef"
)
const handleRequestPortalLink = async () => {
  if (!isSupervisor.value) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()
    if (isCanceled) return
  }

  await portalEmailListDrawerRef.value?.reveal()
}

const onToggleEnableGratuity = async (
  newValue: boolean
) => {
  try {
    const newState = await partialUpdateSettings({
      isGratuityEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle gratuity")
  }
}

const onToggleEnableCash = async (newValue: boolean) => {
  try {
    const newState = await partialUpdateSettings({
      isCashEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle cash")
  }
}

const handleToggleProducts = async (newValue: boolean) => {
  try {
    const newState = await partialUpdateSettings({
      isProductsEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle products")
  }
}

const handleToggleCashiers = async (newValue: boolean) => {
  try {
    const newState = await partialUpdateSettings({
      isCashiersEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle cashiers")
  }
}

const handlePrintXReportClick = async () => {
  if (!isSupervisor.value) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()
    if (isCanceled) return
  }

  startLoading()
  try {
    if (platform === "ingenicoNerpay") {
      await IReports.printXReport()
    } else if (platform === "paxPositive") {
      const report = await IReports.generateXReport()
      await printPositiveXReport(report)
    }
  } catch {
    toast.error("Failed to print X Report")
  } finally {
    stopLoading()
  }
}

const zReportDrawerIsOpen = ref(false)

const handleZReportClick = async () => {
  if (!isSupervisor.value) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()
    if (isCanceled) return
  }

  switch (platform) {
    case "ingenicoNerpay": {
      startLoading()
      try {
        await IReports.printZReport({ isAmex: false })
      } catch {
        toast.error("Failed to print Z Report")
      } finally {
        stopLoading()
      }
      break
    }
    case "web":
    case "paxEvo": {
      startLoading()
      try {
        const report = await IReports.generateZReport({
          isAmex: false,
        })
        toast.success("Z report successful")

        const printableReceipt =
          await formatEvoPaymentsZReport(report)

        if (
          !report.terminalPrintedNativeReceipt &&
          printingSupported.value
        ) {
          await IPrinter.printStr({ str: printableReceipt })
        }

        const { id: zReportId } = await logZReport(
          printableReceipt,
          report
        )

        router.push({
          name: "settingsZReportDetail",
          params: {
            zReportId: zReportId,
          },
          state: {
            backRoute: {
              name: route.name,
              params: route.params,
            },
          },
        })
      } catch (error) {
        if (error instanceof HandledError) return
        if (error instanceof ApiError) {
          toast.error(
            "Failed to save Z report receipt to server"
          )
          return
        }
        Sentry.captureException(error)
        toast.error("Failed to generate Z Report")
      } finally {
        stopLoading()
      }
      break
    }
    case "paxPositive": {
      zReportDrawerIsOpen.value = true
      break
    }
  }
}

const endOfDayReport = ref("")
const endOfDayReportIsOpen = ref(false)

const handleEndOfDayReportClick = async () => {
  if (!isSupervisor.value) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()
    if (isCanceled) return
  }

  startLoading()
  try {
    const report = await retreiveEndOfDayReport()
    if (printingSupported.value) {
      await printEndOfDayReport(report)
    } else {
      endOfDayReport.value = formatEndOfDayReport(report)
      endOfDayReportIsOpen.value = true
    }
  } catch {
    toast.error(
      printingSupported.value
        ? "Failed to print end of day report"
        : "Failed to load end of day report"
    )
  } finally {
    stopLoading()
  }
}

const unlinkedRefundDrawerRef = useTemplateRef(
  "unlinkedRefundDrawerRef"
)
const handleOpenUnlinkedRefundDrawer = async () => {
  if (!isSupervisor.value) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()
    if (isCanceled) return
  }

  await unlinkedRefundDrawerRef.value?.reveal()
}

const updateSetting = async (
  key: keyof TenantState,
  value: TenantState[typeof key]
) => {
  try {
    const newState = await partialUpdateSettings({
      [key]: value,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to update setting")
  }
}
</script>
